import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Skill } from '../../types';

interface SkillDetailModalProps {
  skill: Skill;
  onClose: () => void;
}

// Dettagli aggiuntivi per ogni skill
const SKILL_DETAILS: Record<string, { description: string; experience: string; projects: string[] }> = {
  // Linguaggi & Framework
  csharp: {
    description: 'Linguaggio principale per lo sviluppo backend e desktop. Utilizzato per applicazioni aziendali, web API e automazione industriale.',
    experience: '10+ anni di esperienza professionale',
    projects: ['STS_730PRECO 5.0', 'Automazione magazzini', 'Sistemi gestionali'],
  },
  vbnet: {
    description: 'Linguaggio per lo sviluppo .NET, principalmente per manutenzione codice legacy e applicazioni desktop.',
    experience: '8+ anni di esperienza',
    projects: ['Test730P', 'Applicazioni Windows Forms'],
  },
  python: {
    description: 'Scripting, data analysis e automazione. Utilizzato per script di sistema e prototipazione rapida.',
    experience: '4 anni di esperienza',
    projects: ['Script di automazione', 'Data processing'],
  },
  php: {
    description: 'Sviluppo backend web, gestione form e integrazione database. Utilizzato per siti dinamici e API.',
    experience: '6 anni di esperienza',
    projects: ['Siti web dinamici', 'Backend API', 'Gestione CMS'],
  },
  javascript: {
    description: 'Sviluppo frontend moderno e applicazioni web interattive. React, vanilla JS e framework moderni.',
    experience: '5+ anni di esperienza',
    projects: ['Portfolio', 'WBall', 'CalCost', 'Gantt'],
  },
  css: {
    description: 'Styling avanzato, responsive design, animazioni CSS e Tailwind CSS. Layout moderni con Flexbox e Grid.',
    experience: '7 anni di esperienza',
    projects: ['Portfolio responsive', 'Tailwind CSS styling', 'Dark mode'],
  },
  // Database
  mysql: {
    description: 'Database relazionale open-source. Utilizzato per applicazioni web e sistemi di gestione.',
    experience: '10+ anni di esperienza',
    projects: ['Sistemi gestionali', 'WBall (Supabase)'],
  },
  oracle: {
    description: 'Database enterprise Oracle. Utilizzato per applicazioni mission-critical e data warehouse.',
    experience: '5 anni di esperienza',
    projects: ['Sistemi enterprise', 'Data warehouse'],
  },
  sqlserver: {
    description: 'Database enterprise Microsoft. Utilizzato per applicazioni aziendali critiche.',
    experience: '8+ anni di esperienza',
    projects: ['Sistemi ERP', 'Data warehouse'],
  },
  access: {
    description: 'Database Desktop Microsoft Access. Utilizzato per applicazioni desktop e gestione dati piccoli/medi.',
    experience: '9 anni di esperienza',
    projects: ['Applicazioni desktop', 'Gestione inventari'],
  },
  // Sistemi
  windows: {
    description: 'Amministrazione server Windows, configurazione AD, GPO e manutenzione sistemi.',
    experience: '12+ anni di esperienza',
    projects: ['Amministrazione rete aziendale', 'Active Directory'],
  },
  linux: {
    description: 'Amministrazione server Linux, deployment applicazioni e configurazione servizi.',
    experience: '8+ anni di esperienza',
    projects: ['VPS nginx deployment', 'Server web walterdev.eu'],
  },
  vmware: {
    description: 'Virtualizzazione server con VMware ESXi. Gestione VM, clustering e disaster recovery.',
    experience: '7 anni di esperienza',
    projects: ['Infrastruttura virtualizzata', 'Backup e recovery'],
  },
  hyperv: {
    description: 'Virtualizzazione Microsoft Hyper-V. Gestione macchine virtuali in ambiente Windows.',
    experience: '6 anni di esperienza',
    projects: ['Infrastruttura VM Windows', 'Replication storage'],
  },
  // Network & Infrastructure
  tcpip: {
    description: 'Protocolli di rete fundamentali, routing, subnetting e troubleshooting.',
    experience: '12+ anni di esperienza',
    projects: ['Configurazione infrastrutture', 'Troubleshooting rete'],
  },
  firewall: {
    description: 'Configurazione firewall, regole di sicurezza, VPN e protezione perimetrale.',
    experience: '10 anni di esperienza',
    projects: ['Configurazione security', 'VPN setup'],
  },
  dns: {
    description: 'Amministrazione DNS, gestione zone, record A/MX/CNAME e troubleshooting risoluzioni.',
    experience: '10 anni di esperienza',
    projects: ['Infrastruttura DNS', 'Gestione domini'],
  },
  dhcp: {
    description: 'Configurazione DHCP server, gestione pool IP e lease management.',
    experience: '9 anni di esperienza',
    projects: ['Infrastruttura rete', 'Allocazione IP automatica'],
  },
  fortigate: {
    description: 'Firewall Fortinet Fortigate. Configurazione security, threat protection e VPN.',
    experience: '4 anni di esperienza',
    projects: ['Perimeter security', 'Advanced threat protection'],
  },
  structuredcabling: {
    description: 'Progettazione e installazione cablaggio strutturato. Cat5e, Cat6, Cat6a e fiber optic.',
    experience: '10 anni di esperienza',
    projects: ['Infrastrutture cablaggio', 'Data center setup'],
  },
  fiberoptic: {
    description: 'Tecnologie fibra ottica, fusion splice, testing e troubleshooting.',
    experience: '7 anni di esperienza',
    projects: ['Connessioni lunghe distanze', 'Backbone infrastruttura'],
  },
  wirelesslongrange: {
    description: 'Connessioni wireless punto-punto, configurazione antenne e radio link.',
    experience: '6 anni di esperienza',
    projects: ['Connessioni campus', 'Siti remoti'],
  },
  // Industriale
  plcsiemens: {
    description: 'Programmazione PLC Siemens S7-300/S7-1200/S7-1500. Step 7 e TIA Portal.',
    experience: '7 anni di esperienza',
    projects: ['Automazione industriale', 'Linee di produzione'],
  },
  rfid: {
    description: 'Sistemi RFID per tracciamento e automazione magazzini. Integrazione con software gestionale.',
    experience: '8+ anni di esperienza',
    projects: ['Automazione magazzini RFID', 'Sistemi di tracciamento'],
  },
  automagazzini: {
    description: 'Automazione magazzini completa. Sistemi AS/RS, conveyor e software di gestione magazzino.',
    experience: '9 anni di esperienza',
    projects: ['Logistica automatizzata', 'WMS integration'],
  },
  arduino: {
    description: 'Programmazione Arduino per prototipi e progetti IoT. Sensori, attuatori e comunicazione.',
    experience: '4 anni di esperienza',
    projects: ['Prototipazione IoT', 'Smart systems'],
  },
  // Web & UI/UX
  pwa: {
    description: 'Progressive Web Apps per esperienze web offline-first e app-like.',
    experience: '3 anni di esperienza',
    projects: ['CalCost PWA', 'WBall offline'],
  },
  dashboard: {
    description: 'Sviluppo dashboard interattive con data visualization, real-time updates e responsive design.',
    experience: '6 anni di esperienza',
    projects: ['Analytics dashboard', 'Monitoring systems'],
  },
  grafici: {
    description: 'Grafici dinamici e data visualization. Chart.js, D3.js e librerie di charting moderne.',
    experience: '5 anni di esperienza',
    projects: ['Dashboard analitiche', 'Report visualization'],
  },
  // Dev Tools
  git: {
    description: 'Controllo versione, branching strategy, merge management e CI/CD.',
    experience: '8+ anni di esperienza',
    projects: ['Tutti i progetti Git-based'],
  },
  visualstudio2019: {
    description: 'IDE Microsoft per sviluppo .NET, C#, VB.NET. Debugging, profiling e extensioni.',
    experience: '8 anni di esperienza',
    projects: ['STS_730PRECO', 'Applicazioni desktop'],
  },
  visualstudio2022: {
    description: 'IDE Microsoft di nuova generazione. Miglioramenti performance, Copilot integration.',
    experience: '2 anni di esperienza',
    projects: ['Progetti moderni C#', 'ASP.NET Core'],
  },
  visualstudio2026: {
    description: 'Ultima versione di Visual Studio con AI integrato nativamente, performance ottimizzate e nuove funzionalità di sviluppo.',
    experience: '1 anno di esperienza',
    projects: ['Progetti .NET 9+', 'Sviluppo AI-assisted'],
  },
  claudecode: {
    description: 'AI-powered development assistant di Anthropic. Utilizzato per accelerare sviluppo, refactoring e code generation.',
    experience: 'Esperienza attiva e continua',
    projects: ['Questo portfolio', 'WBall', 'CalCost'],
  },
  llm: {
    description: 'Integrazione Large Language Models come Gemini, Claude, GPT. Prompt engineering e AI features.',
    experience: '2 anni di esperienza',
    projects: ['WBall (Gemini 2.5)', 'CalCost AI consulenza'],
  },
  aitools: {
    description: 'Utilizzo strumenti AI moderni: Claude, ChatGPT, GitHub Copilot e altri AI tools.',
    experience: '2 anni di esperienza',
    projects: ['Sviluppo accelerato', 'AI-assisted programming'],
  },
  davinciresolve: {
    description: 'Software di video editing e color grading professionale. Timeline editing e effects.',
    experience: '3 anni di esperienza',
    projects: ['Video content creation', 'Portfolio videos'],
  },
  mcp: {
    description: 'Model Context Protocol di Anthropic. Integrazione LLM con strumenti e servizi esterni.',
    experience: '1 anno di esperienza',
    projects: ['Claude Code MCP servers', 'Custom integrations'],
  },
};

const SkillDetailModal: React.FC<SkillDetailModalProps> = ({ skill, onClose }) => {
  const details = SKILL_DETAILS[skill.id] || {
    description: 'Competenza tecnica professionale.',
    experience: `Livello: ${skill.level}/5`,
    projects: [],
  };

  const percentage = skill.level * 20;

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                {skill.name}
              </h2>
              <div className="mt-2 flex items-center gap-4">
                <div className="flex-1 max-w-sm">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Livello competenza
                    </span>
                    <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                      {percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-primary-600 to-indigo-600 dark:from-primary-500 dark:to-indigo-500 h-2 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Contenuto */}
          <div className="p-6 space-y-6">
            {/* Descrizione */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Descrizione
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {details.description}
              </p>
            </div>

            {/* Esperienza */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Esperienza
              </h3>
              <div className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 rounded-lg font-medium">
                {details.experience}
              </div>
            </div>

            {/* Progetti correlati */}
            {details.projects.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Progetti correlati
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {details.projects.map((project, idx) => (
                    <motion.div
                      key={project}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg"
                    >
                      <span className="text-primary-600 dark:text-primary-400">→</span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {project}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Livello dettagliato */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Livello dettagliato
                </span>
                <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {skill.level}/5
                </span>
              </div>
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{ delay: i * 0.1 }}
                    className={`h-8 flex-1 rounded ${
                      i < skill.level
                        ? 'bg-gradient-to-t from-primary-600 to-indigo-600 dark:from-primary-500 dark:to-indigo-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SkillDetailModal;
